<script setup>
import { ref, watch, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  lead: { type: Object, default: null },
  isOpen: { type: Boolean, default: false },
  defaultStageId: { type: [Number, String], default: null },
  autoVerify: { type: Boolean, default: false },
  projects: { type: Array, default: () => [] },
  stages: { type: Array, default: () => [] },
  agents: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'saved']);

const store = useStore();
const { t } = useI18n();

const isSaving = ref(false);
const error = ref('');
const contactError = ref('');

const EMPTY_FORM = {
  name: '',
  phone_number: '',
  email: '',
  project_id: '',
  kanban_stage_id: '',
  assigned_agent_id: '',
  source_channel: '',
  budget_min: '',
  budget_max: '',
  currency: 'INR',
  preferred_location: '',
  property_type: '',
  bedrooms: '',
  purpose: '',
  timeline: '',
  priority: 'normal',
  next_follow_up_at: '',
};

const form = ref({ ...EMPTY_FORM });
const isEditing = computed(() => !!props.lead);

watch(
  () => props.isOpen,
  open => {
    if (!open) return;
    error.value = '';
    contactError.value = '';
    if (props.lead) {
      Object.keys(EMPTY_FORM).forEach(k => {
        form.value[k] = props.lead[k] ?? EMPTY_FORM[k];
      });
      if (props.lead.next_follow_up_at)
        form.value.next_follow_up_at = props.lead.next_follow_up_at.slice(
          0,
          16
        );
    } else {
      form.value = { ...EMPTY_FORM };
      if (props.defaultStageId)
        form.value.kanban_stage_id = String(props.defaultStageId);
    }
  }
);

// Auto-fill empty requirement fields when project is selected
watch(
  () => form.value.project_id,
  newId => {
    if (!newId) return;
    const project = props.projects.find(p => String(p.id) === String(newId));
    if (!project) return;
    const loc = project.location || project.city || '';
    if (!form.value.preferred_location && loc)
      form.value.preferred_location = loc;
    if (!form.value.property_type && project.property_type)
      form.value.property_type = project.property_type;
    if (project.currency) form.value.currency = project.currency;
    if (!form.value.budget_min && project.price_min)
      form.value.budget_min = project.price_min;
    if (!form.value.budget_max && project.price_max)
      form.value.budget_max = project.price_max;
    const opts = project.bedroom_options;
    if (!form.value.bedrooms && Array.isArray(opts) && opts.length === 1)
      form.value.bedrooms = opts[0];
  }
);

function validate() {
  contactError.value = '';
  if (!form.value.name.trim() && !form.value.phone_number.trim()) {
    contactError.value = 'Please enter a name or phone number.';
    return false;
  }
  return true;
}

async function onSubmit() {
  if (!validate()) return;
  error.value = '';
  isSaving.value = true;
  try {
    const payload = { ...form.value };
    if (!payload.budget_min) delete payload.budget_min;
    if (!payload.budget_max) delete payload.budget_max;
    if (!payload.project_id) delete payload.project_id;
    if (!payload.kanban_stage_id) delete payload.kanban_stage_id;
    if (!payload.assigned_agent_id) delete payload.assigned_agent_id;
    if (!payload.next_follow_up_at) delete payload.next_follow_up_at;
    [
      'email',
      'source_channel',
      'preferred_location',
      'property_type',
      'bedrooms',
      'purpose',
      'timeline',
    ].forEach(k => {
      if (!payload[k]) delete payload[k];
    });
    if (props.autoVerify) payload.verification_status = 'human_verified';

    if (isEditing.value) {
      await store.dispatch('nexceoLeads/updateLead', {
        id: props.lead.id,
        ...payload,
      });
      useAlert(t('NEXCEO.LEAD.FORM.UPDATED'));
    } else {
      await store.dispatch('nexceoLeads/createLead', payload);
      useAlert(t('NEXCEO.LEAD.FORM.CREATED'));
    }
    emit('saved');
    emit('close');
  } catch (e) {
    const msg = e?.response?.data?.message || e?.message || 'Unknown error';
    const status = e?.response?.status || 'network';
    error.value = `[${status}] ${msg}`;
  } finally {
    isSaving.value = false;
  }
}

const channelOptions = [
  'whatsapp',
  'email',
  'facebook',
  'instagram',
  'web_widget',
  'api',
  'other',
];
const purposeOptions = ['investment', 'self_use', 'rental', 'unknown'];
const timelineOptions = [
  'immediate',
  '1_month',
  '3_months',
  '6_months',
  'unknown',
];
const priorityOptions = ['urgent', 'high', 'normal', 'low'];
const currencyOptions = ['INR', 'USD', 'AED'];
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <!--
    Modal structure — three separate layers:
      1. Backdrop   absolute behind panel — no click handler (avoids open→close race)
      2. Flex wrapper  pointer-events:none so it doesn't intercept clicks
      3. Panel  pointer-events:auto — close only via × or Cancel button
  -->
  <div
    v-if="isOpen"
    class="fixed inset-0 z-[9999]"
    aria-modal="true"
    role="dialog"
  >
    <!-- backdrop -->
    <div class="absolute inset-0 bg-black/50" />

    <!-- centering flex wrapper -->
    <div
      class="absolute inset-0 flex items-center justify-center p-4 pointer-events-none"
    >
      <!-- modal panel -->
      <div
        class="relative bg-white dark:bg-n-solid-2 rounded-xl shadow-2xl w-full max-w-[540px] flex flex-col max-h-[90vh] pointer-events-auto"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak shrink-0"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-user-plus size-4 text-woot-500" />
            <h2 class="text-base font-semibold text-n-slate-12">
              {{
                isEditing
                  ? t('NEXCEO.LEAD.FORM.EDIT_TITLE')
                  : t('NEXCEO.LEAD.FORM.NEW_TITLE')
              }}
            </h2>
          </div>
          <button
            type="button"
            class="text-n-slate-10 hover:text-n-slate-12 p-1 rounded transition-colors"
            @click="$emit('close')"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <!-- Scrollable body -->
        <div class="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <!-- API error -->
          <div
            v-if="error"
            class="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5"
          >
            <span class="i-lucide-alert-circle size-4 shrink-0 mt-0.5" />
            {{ error }}
          </div>

          <!-- Name + Phone (required) -->
          <div>
            <p
              class="text-xs font-semibold text-n-slate-9 mb-2 uppercase tracking-wide"
            >
              Contact <span class="text-red-500">*</span>
            </p>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs text-n-slate-8 mb-1">{{
                  t('NEXCEO.LEAD.FIELDS.NAME')
                }}</label>
                <input
                  v-model="form.name"
                  type="text"
                  :placeholder="t('NEXCEO.LEAD.FIELDS.NAME')"
                  class="w-full text-sm rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-woot-500 border"
                  :class="
                    contactError
                      ? 'border-red-400 bg-red-50'
                      : 'border-n-weak bg-white dark:bg-n-solid-3 text-n-slate-12'
                  "
                  @input="contactError = ''"
                />
              </div>
              <div>
                <label class="block text-xs text-n-slate-8 mb-1">{{
                  t('NEXCEO.LEAD.FIELDS.PHONE')
                }}</label>
                <input
                  v-model="form.phone_number"
                  type="text"
                  :placeholder="t('NEXCEO.LEAD.FORM.PLACEHOLDERS.PHONE')"
                  class="w-full text-sm rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-woot-500 border"
                  :class="
                    contactError
                      ? 'border-red-400 bg-red-50'
                      : 'border-n-weak bg-white dark:bg-n-solid-3 text-n-slate-12'
                  "
                  @input="contactError = ''"
                />
              </div>
            </div>
            <p
              v-if="contactError"
              class="mt-1.5 text-xs text-red-600 flex items-center gap-1"
            >
              <span class="i-lucide-alert-circle size-3.5" />{{ contactError }}
            </p>
            <p v-else class="mt-1 text-xs text-n-slate-6">
              At least name or phone is required.
            </p>
          </div>

          <!-- Email -->
          <div>
            <label class="block text-xs text-n-slate-8 mb-1">{{
              t('NEXCEO.LEAD.FIELDS.EMAIL')
            }}</label>
            <input
              v-model="form.email"
              type="email"
              :placeholder="t('NEXCEO.LEAD.FORM.PLACEHOLDERS.EMAIL')"
              class="w-full text-sm border border-n-weak bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-woot-500"
            />
          </div>

          <!-- Project + Stage -->
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">{{
                t('NEXCEO.LEAD.FIELDS.PROJECT')
              }}</label>
              <select
                v-model="form.project_id"
                class="w-full text-sm border border-n-weak bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-woot-500"
              >
                <option value="">— any project</option>
                <option v-for="p in projects" :key="p.id" :value="String(p.id)">
                  {{ p.name }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">{{
                t('NEXCEO.LEAD.FIELDS.STAGE')
              }}</label>
              <select
                v-model="form.kanban_stage_id"
                class="w-full text-sm border border-n-weak bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-woot-500"
              >
                <option value="">— auto (New Enquiry)</option>
                <option v-for="s in stages" :key="s.id" :value="String(s.id)">
                  {{ s.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Channel + Priority -->
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">{{
                t('NEXCEO.LEAD.FIELDS.SOURCE_CHANNEL')
              }}</label>
              <select
                v-model="form.source_channel"
                class="w-full text-sm border border-n-weak bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-woot-500"
              >
                <option value="">— channel</option>
                <option v-for="c in channelOptions" :key="c" :value="c">
                  {{ c }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">{{
                t('NEXCEO.LEAD.FIELDS.PRIORITY')
              }}</label>
              <select
                v-model="form.priority"
                class="w-full text-sm border border-n-weak bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-woot-500"
              >
                <option v-for="p in priorityOptions" :key="p" :value="p">
                  {{ t(`NEXCEO.KANBAN.PRIORITY.${p}`) }}
                </option>
              </select>
            </div>
          </div>

          <!-- Budget -->
          <div>
            <label class="block text-xs text-n-slate-8 mb-1">{{
              t('NEXCEO.LEAD.FIELDS.BUDGET')
            }}</label>
            <div class="flex gap-2">
              <select
                v-model="form.currency"
                class="text-sm border border-n-weak bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-woot-500 w-20 shrink-0"
              >
                <option v-for="c in currencyOptions" :key="c" :value="c">
                  {{ c }}
                </option>
              </select>
              <input
                v-model="form.budget_min"
                type="number"
                min="0"
                :placeholder="t('NEXCEO.LEAD.FORM.PLACEHOLDERS.BUDGET_MIN')"
                class="flex-1 text-sm border border-n-weak bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-woot-500"
              />
              <input
                v-model="form.budget_max"
                type="number"
                min="0"
                :placeholder="t('NEXCEO.LEAD.FORM.PLACEHOLDERS.BUDGET_MAX')"
                class="flex-1 text-sm border border-n-weak bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-woot-500"
              />
            </div>
          </div>

          <!-- Requirement fields -->
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">{{
                t('NEXCEO.LEAD.FIELDS.PREFERRED_LOCATION')
              }}</label>
              <input
                v-model="form.preferred_location"
                type="text"
                :placeholder="t('NEXCEO.LEAD.FORM.PLACEHOLDERS.LOCATION')"
                class="w-full text-sm border border-n-weak bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-woot-500"
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">{{
                t('NEXCEO.LEAD.FIELDS.BEDROOMS')
              }}</label>
              <input
                v-model="form.bedrooms"
                type="text"
                :placeholder="t('NEXCEO.LEAD.FORM.PLACEHOLDERS.BEDROOMS')"
                class="w-full text-sm border border-n-weak bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-woot-500"
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">{{
                t('NEXCEO.LEAD.FIELDS.PROPERTY_TYPE')
              }}</label>
              <input
                v-model="form.property_type"
                type="text"
                :placeholder="t('NEXCEO.LEAD.FORM.PLACEHOLDERS.PROPERTY_TYPE')"
                class="w-full text-sm border border-n-weak bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-woot-500"
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">{{
                t('NEXCEO.LEAD.FIELDS.PURPOSE')
              }}</label>
              <select
                v-model="form.purpose"
                class="w-full text-sm border border-n-weak bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-woot-500"
              >
                <option value="">—</option>
                <option v-for="p in purposeOptions" :key="p" :value="p">
                  {{ p.replace('_', ' ') }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">{{
                t('NEXCEO.LEAD.FIELDS.TIMELINE')
              }}</label>
              <select
                v-model="form.timeline"
                class="w-full text-sm border border-n-weak bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-woot-500"
              >
                <option value="">—</option>
                <option v-for="tl in timelineOptions" :key="tl" :value="tl">
                  {{ tl.replace(/_/g, ' ') }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">{{
                t('NEXCEO.LEAD.FIELDS.ASSIGNED_AGENT')
              }}</label>
              <select
                v-model="form.assigned_agent_id"
                class="w-full text-sm border border-n-weak bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-woot-500"
              >
                <option value="">—</option>
                <option v-for="a in agents" :key="a.id" :value="a.id">
                  {{ a.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Next follow-up -->
          <div>
            <label class="block text-xs text-n-slate-8 mb-1">{{
              t('NEXCEO.LEAD.FIELDS.NEXT_FOLLOW_UP')
            }}</label>
            <input
              v-model="form.next_follow_up_at"
              type="datetime-local"
              class="w-full text-sm border border-n-weak bg-white dark:bg-n-solid-3 text-n-slate-12 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-woot-500"
            />
          </div>
        </div>

        <!-- Footer -->
        <div
          class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak shrink-0"
        >
          <button
            type="button"
            class="px-4 py-2 text-sm text-n-slate-9 hover:text-n-slate-12 rounded-lg hover:bg-n-alpha-2 transition-colors"
            @click="$emit('close')"
          >
            {{ t('NEXCEO.LEAD.FORM.CANCEL') }}
          </button>
          <button
            type="button"
            :disabled="isSaving"
            class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-lg disabled:opacity-50 transition-colors"
            @click="onSubmit"
          >
            <span
              v-if="isSaving"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span v-else class="i-lucide-check size-4" />
            {{
              isSaving
                ? 'Saving…'
                : isEditing
                  ? t('NEXCEO.LEAD.FORM.SAVE')
                  : t('NEXCEO.LEAD.FORM.CREATE')
            }}
          </button>
        </div>
      </div>
      <!-- /panel -->
    </div>
    <!-- /centering wrapper -->
  </div>
  <!-- /v-if isOpen -->
</template>
